import { useEffect, useState } from "react";
import { motion } from "@/lib/motion";
import {
    ServerCog,
    ShieldCheck,
    Cloud,
    Compass,
    Sparkles,
    DatabaseBackup,
    ArrowUpRight,
} from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { getHomepageServices } from "../lib/sanityQueries";
import { peek } from "../lib/sanity";

const services = [
    {
        icon: ShieldCheck,
        serviceId: "service-cybersecurity",
        title: "Cybersecurity & Risk",
        body: "DLP, threat detection, compliance, and incident response — protection engineered around your business risk and built into every engagement.",
        span: "lg:col-span-7",
        feature: true,
        tags: ["Threat Detection", "DLP & Vault", "Incident Response"],
    },
    {
        icon: Compass,
        serviceId: "service-vcio",
        title: "Technology Advisory (vCIO)",
        body: "Executive-level guidance — technology roadmaps, vendor management, and AI governance that turn technology into a deliberate strategy.",
        span: "lg:col-span-5",
    },
    {
        icon: ServerCog,
        serviceId: "service-managed-it",
        title: "Managed IT & Operations",
        body: "24/7 service desk, proactive monitoring, and endpoint management that keep operations running — predictive, not reactive.",
        span: "lg:col-span-5",
    },
    {
        icon: Cloud,
        serviceId: "service-cloud",
        title: "Cloud & Infrastructure",
        body: "Google Workspace, backup and recovery, and seamless migrations on cloud-first infrastructure built to scale with demand.",
        span: "lg:col-span-7",
        feature: true,
        tags: ["Google Workspace", "Backup & Recovery", "Migrations"],
    },
    {
        icon: Sparkles,
        serviceId: "service-digital-experience",
        title: "Digital Transformation & AI",
        body: "Custom integrations, workflow automation, and system optimization — so you can adopt AI safely, with governance built in.",
        span: "lg:col-span-6",
    },
    {
        icon: DatabaseBackup,
        serviceId: "service-business-continuity-resilience",
        title: "Business Continuity",
        body: "Someone accountable when systems fail — continuity ownership that keeps your business operational, whatever happens.",
        span: "lg:col-span-6",
    },
];

export const Services = () => {
    const [data, setData] = useState(() => peek(getHomepageServices));
    useEffect(() => {
        let active = true;
        getHomepageServices()
            .then((res) => {
                if (active && res) setData(res);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    const byId = Object.fromEntries((data?.services || []).map((s) => [s._id, s]));

    return (
    <section
        id="services"
        data-testid="services-section"
        className="relative border-y border-brand-line bg-brand-surface py-28 sm:py-36"
    >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="mb-14 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
                <div>
                    <Reveal>
                        <Overline>Capabilities</Overline>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2 className="mt-5 max-w-2xl font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                            {data?.servicesHeadline || "One partner across the full technology surface."}
                        </h2>
                    </Reveal>
                </div>
                <Reveal delay={0.1}>
                    <p className="max-w-sm text-sm leading-relaxed text-brand-muted">
                        {data?.servicesIntro ||
                            "Integrated capabilities — not disconnected services — delivered by a team accountable for the whole picture."}
                    </p>
                </Reveal>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                {services.map((s, i) => {
                    const doc = byId[s.serviceId];
                    const title = doc?.name || s.title;
                    const body = doc?.shortDescription || s.body;
                    return (
                    <Reveal
                        key={s.serviceId || s.title}
                        delay={(i % 2) * 0.06}
                        className={s.span}
                    >
                        <motion.div
                            whileHover={{ y: -6 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            data-testid={`service-card-${i}`}
                            className="group relative h-full overflow-hidden rounded-3xl border border-brand-line bg-brand-bg p-7 transition-colors duration-150 hover:border-brand-accent/40 sm:p-9"
                        >
                            <div className="flex items-start justify-between">
                                <span className="grid h-14 w-14 place-items-center rounded-2xl border border-brand-line bg-brand-surface2 text-brand-accent transition-colors duration-150 group-hover:bg-brand-accent group-hover:text-brand-on-accent">
                                    <s.icon className="h-6 w-6" />
                                </span>
                                <ArrowUpRight className="h-5 w-5 text-brand-muted opacity-0 transition-colors duration-150 group-hover:opacity-100" />
                            </div>
                            <h3 className="mt-7 font-heading text-2xl font-bold tracking-tight text-brand-text">
                                {title}
                            </h3>
                            <p className="mt-3 max-w-md text-sm leading-relaxed text-brand-muted">
                                {body}
                            </p>
                            {s.tags && (
                                <div className="mt-6 flex flex-wrap gap-2">
                                    {s.tags.map((t) => (
                                        <span
                                            key={t}
                                            className="rounded-full border border-brand-line bg-brand-surface px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-brand-muted"
                                        >
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
    );
};
